/* ============================================================================
   cloudtec Plattform-Konfigurator — Configuration
   ----------------------------------------------------------------------------
   Alle modell-relevanten Konstanten an einem Ort. Spiegelt 02-config.yaml aus
   doc/. Wenn cloudtec das Modell rekalibriert, hier (und in der YAML) ändern.

   Eine spätere Backend-Integration kann diese Datei aus 02-config.yaml
   generieren — das Schema bleibt identisch (snake_case-Keys auf JS-Seite als
   camelCase, Werte 1:1).
   ============================================================================ */

const CFG = {
  meta: {
    schemaVersion: 1,
    zuletztKalibriertAm: '2026-05-08',
  },

  // ── Mischsatz ────────────────────────────────────────────────────────────
  mischsatz: {
    chfProStunde: 180,
    chfProPt: 1440,
    stundenProPt: 8,
    waehrung: 'CHF',
    exklMwst: true,
  },

  // ── Aufwandsformel: CapP × stundenProCapp × CM × UF ──────────────────────
  baseProduktivitaet: {
    stundenProCapp: 9,   // Hauptstellschraube (v5: 18→10 anhand Referenzprojekte)
  },

  // CapP-Gewichte — pro gewähltem Item.
  // Hinweis: Die 12 Basis-Funktionen (MODULKATALOG.basis) zählen NICHT in CapP,
  // sie sind im Stack integriert. Hier wirken nur die 15 wählbaren `module`,
  // die 4 `hilfsfunktionen`, die 4 KI-Funktionen und die Stack-CMS-Pakete.
  cappGewichte: {
    module:               4,   // pro gewähltem MODULKATALOG.module
    hilfsfunktionen:      2,   // pro gewählter MODULKATALOG.hilfsfunktion
    customCore:          11,   // pro customCore-Einheit (höher gewichtet als Standardmodul)
    customSupport:        6,   // pro customSupport-Einheit
    integrationsgewicht:  0.05, // × Σ(cappDirektion × kritikalitaet) — v5: 2→1→0.5
    datengewicht:         1.0, // × Σ über Domänen (volumen + sensitivitaet) — v5: 3→1.5
    migrationsstroeme:    5,   // × Anzahl Migrationsströme (wenn migration=yes)
    // KI-Funktionen (individuelle Gewichte pro Item)
    kiFunktionen: {
      'ai-gateway':   10,   // Zentrale LLM-Integrationsschicht
      'ai-pipelines': 15,   // RAG, Vector-DB, Embedding-Pipeline, Doc-AI on-prem
      'ai-agents':    12,   // Tool-Framework, Workflow-Engine, Human-in-the-Loop
      'ai-ux':        10,   // KI-Funktionen im UI (Auto-Fill, semantische Suche)
    },
    // Stack CMS by cloudtec (Basis + 3 Add-ons; Add-ons erfordern Basis)
    stackCms: {
      'stack-cms':         18,   // Basis-Integration, Themes, Templates, Mandanten
      'stack-ai-writing':   6,   // Writing Assistant, KI-Übersetzung, SEO-Profile
      'stack-dashboard':    4,   // Vollständigkeit, Broken-Links, Kohärenz
      'stack-ai-ready':     6,   // RAG-Setup mit Kundeninhalten, MCP-Endpoint
    },
  },

  // ── Complexity Index — 10 Kategorien, Summe der Gewichte = 1.00 ──────────
  // v5 (2026-05): drittanbieter (0.05) und deployment (0.06) entfernt — wurden
  // nicht in der UI erfasst. Die freigewordenen 0.11 sind auf die verbleibenden
  // Kategorien verteilt.
  complexityIndex: {
    funktional: 0.20,
    nichtFunktional: 0.08,
    integration: 0.13,
    daten: 0.09,
    nutzerskalierung: 0.05,
    sicherheit: 0.13,
    compliance: 0.08,
    teamSkill: 0.05,
    wartung: 0.06,
    ki: 0.13,
  },

  // CM = basis + faktor × (CI / 100). v5 stark gespreizt anhand Referenzen:
  // CI=0 → 0.20, CI=100 → 2.60. Erfasst grosse Spannweite zwischen Klein- und Grossprojekten.
  complexityMultiplier: {
    basis: 0.10,
    faktor: 3.50,
  },

  complexityBaender: {
    sehrNiedrigBis: 20,
    niedrigBis: 40,
    mittelBis: 60,
    hochBis: 80,
    // > 80 → sehr_hoch
  },

  // ── Score-Mapping pro Frage (Score 0..4) ─────────────────────────────────
  // Jede der 11 CI-Kategorien hat eigene Bonus-Tabellen und Schwellen.
  // schwellen = [t1, t2, t3, t4] → raw < t1 → 0,  raw < t2 → 1,  …  raw ≥ t4 → 4
  // Alles, was ein User auswählen kann, hat hier seinen Beitrag.
  scoring: {

    // ── 1. Funktional (CI-Gewicht 0.18) — Module + Customizing ──────────
    funktional: {
      customCoreFaktor:    0.4,   // pro p1.customCore Element (zusätzlich zu p1.modules)
      customSupportFaktor: 0.15,  // pro p1.customSupport Element
      customComplexityBonus: { easy: 0, medium: 1, complex: 2 },
      schwellen: [1.5, 4, 8, 11],
    },

    // ── 2. Nicht-funktional (0.12) — Performance / Audit / Sprache ──────
    nichtFunktional: {
      languagesBonus:    { none: 0, two: 1, many: 2 },
      performanceBonus:  { standard: 0, latency: 1, strict: 2 },
      auditabilityBonus: { standard: 0, extended: 1, regulatory: 2 },
      schwellen: [1, 3, 5, 6],
    },

    // ── 3. Integration (0.12) ───────────────────────────────────────────
    // Hinweis: Für CapP wirkt `cappDirektionFaktor` × `kritikalitaetFaktor`,
    // für den Score `scoreDirektionFaktor` × `kritikalitaetFaktor`.
    // Bidirectional zählt in CapP härter (2.0) als im Score (1.5).
    integration: {
      kritikalitaetFaktor: { 'nice-to-have': 1, wichtig: 2, kritisch: 3 },
      cappDirektionFaktor:  { read: 1, write: 1, bidirectional: 2 },
      scoreDirektionFaktor: { read: 1, write: 1, bidirectional: 1.5 },
      schwellen: [1, 6, 12, 17],
    },

    // ── 4. Daten (0.09) ─────────────────────────────────────────────────
    // CapP-Approximation: pro Domäne (Volumen-Annahme + Sensitivität).
    // Score: Domänen-Anzahl + Migrations-Bonus + Sensitivität + dataQuality + migrationPath.
    daten: {
      cappVolumenAnnahme:     2,   // Volumen pro Domäne (Mittel-Wert mangels Spec)
      cappSensitivitaetBonus: { public: 1, internal: 1, personal: 2, sensitive: 3 },
      scoreSensitivitaetBonus:{ public: 0, internal: 0, personal: 1, sensitive: 2 },
      migrationBaseBonus:   1,     // wenn migration === 'yes': +1
      migrationStreamFaktor:0.4,   // + min(2, streams × 0.4)
      migrationStreamMax:   2,
      // Migrations-Detail-Boni (gelten nur wenn migration === 'yes')
      dataQualityBonus:    { high: 0, mixed: 0.5, low: 1.5 },
      migrationPathBonus:  { bigbang: 0, phased: 0.5, parallel: 1.0 },
      schwellen: [2, 4, 6.5, 9],
    },

    // ── 5. Nutzerskalierung (0.08) ──────────────────────────────────────
    nutzerskalierung: {
      activeUsersBonus:     { small: 0, medium: 1, large: 2, huge: 3, massive: 4 },
      peakConcurrencyBonus: { '50': 0, '500': 1, '5k': 2, '5k+': 3 },
      tenantModelBonus:     { single: 0, 'multi-few': 1, 'multi-mid': 2, 'multi-many': 3 },
      schwellen: [1, 3, 5, 7],
    },

    // ── 6. Sicherheit (0.10) ────────────────────────────────────────────
    sicherheit: {
      standardLoginAlleinBonus: 0.5,  // nur standard_login gewählt → 0.5 statt 1
      schwellen: [1, 3, 5, 7],
    },

    // ── 7. Compliance (0.08) ────────────────────────────────────────────
    compliance: {
      residencyBonus: { ch: 0, eu: 0.5, global: 1 },
      schwellen: [1, 2.5, 4, 5],
    },

    // ── 8. Team-Skill (0.06) ────────────────────────────────────────────
    // Hidden-Layer: cloudtec-Plattformerfahrung fix hoch.
    // Variabel: Projekt-/Unternehmensart, Custom-Komplexität, Mehrsprachigkeit.
    teamSkill: {
      projectKindBonus:      { green: 1.5, new: 0, modern: 0, rebuild: 0 },
      companyKindBonus:      { startup: 1, sme: 0, estab: 0, corp: 1 },
      customComplexityBonus: { easy: 0, medium: 0, complex: 1.5 },
      languagesBonus:        { none: 0, two: 0, many: 0.5 },
      schwellen: [0.5, 1.5, 2.5, 3.5],
    },

    // ── 9. Wartung (0.06) ───────────────────────────────────────────────
    wartung: {
      availabilityBonus: { standard: 0, redundant: 1, 'low-downtime': 2.5, 'full-ha': 4 },
      schwellen: [0.5, 1.5, 2.5, 3.5],
    },

    // ── 10. KI / Stack CMS (0.13) — neu seit v4 ──────────────────────────
    // Score = Σ itemBonus über alle in p1.ai[] + p1.stackcms[]
    ki: {
      itemBonus: {
        // KI-Funktionen — Komplexität proportional zu Engineering-Aufwand
        'ai-gateway':   1.0,
        'ai-pipelines': 2.0,   // RAG, Vector-DB, MLOps — höchste Komplexität
        'ai-agents':    2.0,   // Workflow-Engine + HITL + Audit — höchste Komplexität
        'ai-ux':        1.0,
        // Stack CMS
        'stack-cms':         1.0,
        'stack-ai-writing':  1.0,
        'stack-dashboard':   0.5,
        'stack-ai-ready':    1.5,   // RAG-Setup mit Kundeninhalten
      },
      schwellen: [0.5, 2, 4, 6],
    },
  },

  // ── Uncertainty Index ────────────────────────────────────────────────────
  uncertaintyIndex: {
    greenfieldAufschlag: 15,            // pre.projectKind === 'green'
    rebuildAufschlag: 10,               // pre.projectKind === 'rebuild'
    weissNichtProAntwort: 5,            // pro Feld mit „Weiss nicht"
    agentLowConfidenceProFeld: 3,       // pro nicht bestätigtem KI-Feld
    maxUi: 100,
  },

  uncertaintyFactor: {
    basis: 1.0,
    faktor: 0.25,
  },

  konfidenzband: {
    basisProzent: 15,
    faktorProzent: 35,
    konfidenzLabel: 'Konfidenz 90%',
  },

  // ── T-Shirt-Sizes (Stunden-basiert) ──────────────────────────────────────
  tShirtSizes: [
    { label: 'S',  bisStunden: 1500,  beschreibung: 'Kleines Plattformprojekt' },
    { label: 'M',  bisStunden: 5000,  beschreibung: 'Mittelgrosses Plattformprojekt' },
    { label: 'L',  bisStunden: 10000, beschreibung: 'Grosses Plattformprojekt' },
    { label: 'XL', bisStunden: 99999, beschreibung: 'Sehr grosses Plattformvorhaben' },
  ],

  // Edge: ein Aufwand, der dieses Punktbudget überschreitet, sprengt den Konfigurator-Scope.
  konfiguratorObergrenzeChf: 1000000,

  // ── Phasenanteile (Summe = 1.00) ─────────────────────────────────────────
  phasenanteile: {
    discovery: 0.14,
    design: 0.16,
    build: 0.34,
    integrationMigration: 0.18,
    testHardening: 0.12,
    deploymentHypercare: 0.06,
  },
  phasenLabels: {
    discovery: 'Discovery & Requirements',
    design: 'Solution & Platform Design',
    build: 'Build & Konfiguration',
    integrationMigration: 'Integration & Migration',
    testHardening: 'Test & Hardening',
    deploymentHypercare: 'Deployment & Hypercare',
  },

  // ── Slider-Bereiche ──────────────────────────────────────────────────────
  projektbudget:        { min: 100000, max: 1000000, schritt: 5000 },
  finanzierungszeitraum:{ minJahre: 1, maxJahre: 5, defaultJahre: 3 },
  anzahlung:            { minProzent: 35, maxProzent: 100, defaultProzent: 50 },
  weiterentwicklung:    { minProzent: 0,  maxProzent: 50,  defaultProzent: 15 },

  // ── Fixe Anteile vom Projektbudget pro Jahr (ab Go-Live) ─────────────────
  fixeJahreskosten: {
    hostingProzent: 3.5,
    lizenzWartungProzent: 10.0,
    serviceManagementProzent: 5.0,
  },

  // ── Service-Optionen (Betriebsphase, Add-on, beeinflussen Aufwand NICHT) ─
  // Quelle: SLA-Template 2025 (doc)
  serviceOptionen: {
    sla: {
      none:    { chfProJahr: 0,    label: 'Kein SLA',          beschreibung: 'Best-Effort, keine garantierten Reaktionszeiten' },
      basis:   { chfProJahr: 1500, label: 'Basis',             beschreibung: 'Mo-Fr 9-17, 99.5% Verfügbarkeit, 1 Werktag proaktiv' },
      weekend: { chfProJahr: 7500, label: 'Weekend',           beschreibung: 'Mo-So 8-18, 99.8% Verfügbarkeit, ½ Werktag proaktiv' },
      vip:     { chfProJahr: 7500, label: 'VIP',               beschreibung: 'Tage gemäss SLA 8-18, 99.8% Verfügbarkeit, 1 Stunde proaktiv' },
    },
    seoChecks:        { chfProJahr: 5000, label: 'SEO & KI-Sichtbarkeit',  beschreibung: 'Monatliche SEO- und KI-Sichtbarkeits-Checks + 1 jährliches Review-Meeting; einmalig CHF 3’600 Initiale Aufschaltung' },
    dataProtection:   { chfProJahr: 2500, label: 'Datenschutz-Beratung', beschreibung: 'Laufende Compliance, Cookiebot-Konfiguration, automatisierte Kontrollen; einmalig CHF 2’880 Initiale Aufschaltung' },
    securityScanning: {
      label: 'Security Scanning',
      beschreibung: 'Audit-fähige Sicherheitsprüfung mit Dokumentation, Bewertung und Re-Scan; Yearly/Quarterly/Monthly als Fixpreis-Pakete.',
      pakete: { none: 0, yearly: 7500, quarterly: 10000, monthly: 15000 },
    },
  },

  // ── Beratungsoptionen (einmalig, Vorprojekt, Year 0, beeinflussen Aufwand NICHT) ──
  beratungsoptionen: {
    advisoryDesign: {
      label: 'Design-centric Approach',
      beschreibung: 'Empfohlener Vorprojekt-Ansatz, ohne Verpflichtung buchbar.',
      empfohlen: true,
      pakete: { none: 0, s: 5, m: 10, l: 20 },
    },
    advisorySpec: {
      label: 'Grobe Spezifikation',
      beschreibung: 'Alternative: strukturierte Anforderungs-Aufnahme.',
      empfohlen: false,
      pakete: { none: 0, s: 5, m: 10, l: 20 },
    },
    advisoryPrototype: {
      label: 'Prototyping',
      beschreibung: 'Klickbarer/Live-Prototyp vor dem Build; Kosten werden bei Projektabschluss vollständig auf das Build-Budget angerechnet.',
      empfohlen: false,
      pakete: { none: 0, s: 10, m: 20, l: 30 },
    },
  },

  // ── Konfigurations-Schlüssel — Alphabet für generateConfigCode() (calc.js) ──
  // Erzeugt den teilbaren Code CT-XXXX-XXXX-XXXX (Footer + Calendly-Deeplink).
  // Ambiguitätsfreies Alphabet (kein I/O/0/1).
  konfigurationsschluessel: {
    zeichen: 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789',
  },

  // ── Limits ───────────────────────────────────────────────────────────────
  limits: {
    beschreibungMaxZeichen: 5000,
    projekttitelMaxZeichen: 120,
  },

  // ── Calendly ─────────────────────────────────────────────────────────────
  // Separate events per locale: Calendly's UI chrome auto-detects browser
  // language, but event title/description is single-language per event type.
  calendly: {
    urlDe: 'https://calendly.com/cloudtec-chris/30-minuten-discovery',
    urlEn: 'https://calendly.com/cloudtec-chris/30-min-discovery-call',
  },

  // ── API endpoint (KI-Vorbefüllung backend) ───────────────────────────────
  api: {
    base: CLOUDTEC_API_BASE,
    prefillPath: '/agent/prefill',
  },
};

// ────────────────────────────────────────────────────────────────────────────
// Modulkatalog — Stand 2026-05 (v3)
// Reorg: Admin UI / Medien / Datenexport / Notifications wurden in v2 in die
// Basis-Funktionen verschoben. Diese zählen NICHT in CapP (sind immer dabei).
// ────────────────────────────────────────────────────────────────────────────

const MODULKATALOG = {
  // 12 Basis-Funktionen, immer enthalten, nicht abwählbar, zählen NICHT in CapP
  basis: [
    { id: 'user',     icon: 'user-gear',         label: 'Benutzerverwaltung' },
    { id: 'perm',     icon: 'shield-halved',     label: 'Berechtigungen' },
    { id: 'auth',     icon: 'key',               label: 'Authentifizierung' },
    { id: 'data',     icon: 'database',          label: 'Datenmanagement' },
    { id: 'lists',    icon: 'list-ul',           label: 'Listen & Filter' },
    { id: 'api',      icon: 'plug',              label: 'API' },
    { id: 'search',   icon: 'magnifying-glass',  label: 'Suche' },
    { id: 'cache',    icon: 'bolt',              label: 'Caching' },
    { id: 'admin-ui', icon: 'sliders',           label: 'Admin UI' },
    { id: 'media',    icon: 'photo-film',        label: 'Medien' },
    { id: 'export',   icon: 'file-export',       label: 'Datenexport' },
    { id: 'notify',   icon: 'bell',              label: 'Notifications' },
  ],
  // Hilfsfunktionen — auswählbar, zählen als Supportfunktionen (CapP × 5)
  hilfsfunktionen: [
    { id: 'i18n',     icon: 'language',          label: 'Übersetzung' },
    { id: 'reporting',icon: 'chart-line',        label: 'Reporting' },
    { id: 'pdf',      icon: 'file-pdf',          label: 'PDF Engine' },
    { id: 'logging',  icon: 'list-check',        label: 'Logging' },
  ],
  // Module — auswählbar, zählen als Kernfunktionen (CapP × 8)
  // Hinweis: 'cms' wurde durch Stack CMS by cloudtec ersetzt (siehe stackCms).
  module: [
    { id: 'shop',      icon: 'cart-shopping',         label: 'Shop' },
    { id: 'calendar',  icon: 'calendar',              label: 'Kalender' },
    { id: 'support',   icon: 'headset',               label: 'Support' },
    { id: 'workflow',  icon: 'diagram-project',       label: 'Workflow' },
    { id: 'newsletter',icon: 'envelope',              label: 'Newsletter' },
    { id: 'tenant',    icon: 'building',              label: 'Multi-Tenant' },
    { id: 'sign',      icon: 'signature',             label: 'Digital Signature' },
    { id: 'payment',   icon: 'credit-card',           label: 'Payment' },
    { id: 'solr',      icon: 'magnifying-glass-plus', label: 'Solr Search' },
    { id: 'maps',      icon: 'map',                   label: 'Karten' },
    { id: 'sso',       icon: 'right-to-bracket',      label: 'SSO' },
    { id: 'audit',     icon: 'clipboard-list',        label: 'Audit' },
    { id: 'forms',     icon: 'square-check',          label: 'Form Builder' },
    { id: 'apiplat',   icon: 'cloud-arrow-up',        label: 'API-Plattform' },
  ],
  // KI-Funktionen — auswählbar, individuelle CapP-Gewichte (siehe cappGewichte.kiFunktionen)
  ki: [
    { id: 'ai-gateway',   icon: 'route',               label: 'KI-Gateway & LLM-Management' },
    { id: 'ai-pipelines', icon: 'diagram-project',     label: 'Lokale KI-Verarbeitung & Agent Pipelines' },
    { id: 'ai-agents',    icon: 'robot',               label: 'KI-Agenten & Prozessautomatisierung' },
    { id: 'ai-ux',        icon: 'wand-magic-sparkles', label: 'KI-gestützte Benutzeroberflächen & AI UX' },
  ],
  // Stack CMS by cloudtec — Basis + 3 Add-ons (Add-ons setzen Basis voraus)
  stackCms: [
    { id: 'stack-cms',         icon: 'newspaper',             label: 'Stack CMS' },
    { id: 'stack-ai-writing',  icon: 'pen-fancy',             label: 'KI-Redaktion' },
    { id: 'stack-dashboard',   icon: 'chart-column',          label: 'Content Dashboard' },
    { id: 'stack-ai-ready',    icon: 'wand-magic-sparkles',   label: 'AI-Ready Layer' },
  ],
};

// ────────────────────────────────────────────────────────────────────────────
// UI-Auswahllisten (Pre-Step)
// ────────────────────────────────────────────────────────────────────────────

const PLATFORM_TYPES = [
  { id: 'kundenportal',  icon: 'user-group',          label: 'Kundenportal' },
  { id: 'b2b',           icon: 'handshake',           label: 'B2B-Plattform' },
  { id: 'b2c',           icon: 'users',               label: 'B2C-Plattform' },
  { id: 'saas',          icon: 'cloud',               label: 'SaaS-Produkt' },
  { id: 'ehealth',       icon: 'heart-pulse',         label: 'E-Health' },
  { id: 'workflow',      icon: 'diagram-project',     label: 'Workflow / Daten' },
  { id: 'whitelabel',    icon: 'palette',             label: 'White-Label Portal' },
  { id: 'api',           icon: 'plug',                label: 'API-zentriert' },
  { id: 'service',       icon: 'gears',               label: 'Enterprise Website' },
  { id: 'shop',          icon: 'cart-shopping',       label: 'E-Commerce / Shop' },
  { id: 'individual',    icon: 'wand-magic-sparkles', label: 'Individuell' },
  { id: 'other',         icon: 'circle-question',     label: 'Etwas anderes' },
];

const PROJECT_KINDS = [
  { id: 'green',   title: 'Grüne Wiese',                          sub: 'Freie Hand, kein Vorbild' },
  { id: 'new',     title: 'Neues Projekt mit klaren Vorgaben',    sub: 'Anforderungen weitgehend klar' },
  { id: 'modern',  title: 'Modernisierung bestehender Software',  sub: 'Schrittweiser Umbau' },
  { id: 'rebuild', title: 'Rebuild von bestehender Plattform',    sub: 'Neuaufbau, alte Plattform ersetzt' },
];

const COMPANY_KINDS = [
  { id: 'startup', title: 'Startup',       sub: '1–10 Angestellte' },
  { id: 'sme',     title: 'Klein/Mittel',  sub: '11–100 Angestellte' },
  { id: 'estab',   title: 'Etabliert',     sub: '101–500 Angestellte' },
  { id: 'corp',    title: 'Grosskonzern',  sub: 'Über 500 Angestellte' },
];

// Backwards-compatible aliases for JSX consumers
const BASIS_FUNCS = MODULKATALOG.basis;
const HILFS_FUNCS = MODULKATALOG.hilfsfunktionen;
const AI_FUNCS = MODULKATALOG.ki;
const STACKCMS_FUNCS = MODULKATALOG.stackCms;
const MODULES = MODULKATALOG.module;

// Stack-CMS-Validierung: Add-ons setzen die Basis voraus
const STACKCMS_BASIS_ID = 'stack-cms';
const STACKCMS_ADDONS = ['stack-ai-writing', 'stack-dashboard', 'stack-ai-ready'];

// expose
export {
  CFG, MODULKATALOG,
  PLATFORM_TYPES, PROJECT_KINDS, COMPANY_KINDS,
  BASIS_FUNCS, HILFS_FUNCS, AI_FUNCS, STACKCMS_FUNCS, MODULES,
  STACKCMS_BASIS_ID, STACKCMS_ADDONS,
};
